import { SkeletonLoader } from '@toggle/design-system';
import { periods } from '@toggle/toggle';
import React, { useMemo } from 'react';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { PriceMovementChart } from '~/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { useGetInsightPredictions } from '~/services/insights/insights-service';
import { filterPredictions } from '~/services/insights/utils/insight-utils';
import { getPredictionsTableData } from '~/views/turbo-chart/components/insights/utils/insights-utils/insights-utils';
import { PredictionSummaryTable } from '~/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';

const IDX_MAX = 127;

interface InsightChartSectionProps {
  insightId: string;
  bestHorizon?: string;
  isFixedIncome: boolean;
  snakeTs?: { index: number; value: number }[];
}

export const InsightChartSection = ({
  insightId,
  bestHorizon,
  isFixedIncome,
  snakeTs,
}: InsightChartSectionProps) => {
  const { data, isLoading } = useGetInsightPredictions(insightId);
  const predictions = data?.data;

  const predictionByHorizon = useMemo(
    () => getPredictionsTableData(periods, predictions ?? []),
    [predictions]
  );

  if (isLoading) {
    return (
      <SkeletonLoader
        data-testid="insight-chart-loading"
        areas={[
          { styles: { width: '100%', height: 312, borderRadius: '8px' } },
          { styles: { width: '100%', height: 140, borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (!predictions) {
    return <ErrorMessageWrapper variant="error" />;
  }

  return (
    <>
      <PriceMovementChart
        prediction={filterPredictions(predictions, IDX_MAX)}
        snakeTs={snakeTs}
        horizonToIndexOffset={1}
        bestHorizon={bestHorizon}
        isFixedIncome={isFixedIncome}
        useDynamicDaysLabel
      />
      <PredictionSummaryTable
        data={predictionByHorizon}
        isMedian
        horizons={periods}
        isFixedIncome={isFixedIncome}
      />
    </>
  );
};
